import type { IssueParams } from 'components/common/forms/EditableField/types';
import { areObjectIdsEqual, isObjectIdString } from 'helpers/idComparison';
import { DocumentsOnIssueView } from 'presentation/document/documentsOnIssueView';
import { documentsToDocumentsOnIssueView } from 'presentation/document/documentsToDocumentsOnIssueView';
import { DocumentModel } from 'shared/domain/document/documentModel';
import type { IssueModel } from 'shared/domain/issue/issueModel';
import {
  CreatableOnIssueView,
  Identificable,
  LabelledEntity,
  ModifiableOnIssueView,
} from 'shared/types/commonView';
import {
  EventDescriptionPartToViewMapper,
  MentionToViewMapper,
  TextDescriptionToViewMapper,
  SelfMentionToViewMapper,
} from './eventDescription';

export function isRouteProjectSelected(
  routeProjectId: string | undefined,
  selectedProjectId: string | undefined
): boolean {
  return (
    isObjectIdString(routeProjectId) &&
    isObjectIdString(selectedProjectId) &&
    areObjectIdsEqual(selectedProjectId, routeProjectId)
  );
}

/** The events of the view, those being saved shown as loading. */
export function markEventsBeingSaved(
  events: IssueEventOnView[],
  eventsBeingSaved: string[]
): IssueEventOnView[] {
  if (eventsBeingSaved.length === 0) {
    return events;
  }
  return events.map((event) =>
    eventsBeingSaved.some((eventId) => areObjectIdsEqual(eventId, event._id))
      ? { ...event, loading: true }
      : event
  );
}

export interface EstimatedCost {
  cost: string;
  coveredBy: LabelledEntity | undefined;
}

export interface FinalCost {
  outstanding: string;
  settled: string;
  coveredBy: LabelledEntity | undefined;
}

export function isIncomingEmailMessage(
  message: IssueEventOnView | IncomingEmailMessageOnView
): message is IncomingEmailMessageOnView {
  return 'incomingEmailMessageContent' in message;
}

export function isIssueEvent(
  message: IssueEventOnView | IncomingEmailMessageOnView
): message is IssueEventOnView {
  return 'description' in message;
}

export type IssueEventOnView = {
  title: string;
  type: string;
  description: (
    | TextDescriptionToViewMapper
    | MentionToViewMapper
    | SelfMentionToViewMapper
  )[];
  deleted: boolean;
  loading?: boolean;
} & Identificable &
  CreatableOnIssueView &
  ModifiableOnIssueView;

export type IncomingEmailMessageOnView = {
  subject: string;
  incomingEmailMessageContent: string;
  deleted: boolean;
  loading?: boolean;
} & Identificable &
  CreatableOnIssueView &
  ModifiableOnIssueView;

interface Point { x: number; y: number }
export type Area = Point[];

/** What the issue's fields and mentions need to know about the issue. */
export function toIssueParams(issue: IssueModel): IssueParams {
  return {
    issueId: issue._id,
    siteId: issue.primaryData.site,
    processId: issue.process,
    userAccesses: issue.userAccesses,
  };
}

const documentsOnIssueViewCache = new WeakMap<DocumentModel[], DocumentsOnIssueView>();

/** The documents grouped as the issue view shows them; grouped once per list, for all its readers. */
export function toDocumentsOnIssueView(documents: DocumentModel[]): DocumentsOnIssueView {
  let documentsOnView = documentsOnIssueViewCache.get(documents);
  if (!documentsOnView) {
    documentsOnView = documentsToDocumentsOnIssueView(documents);
    documentsOnIssueViewCache.set(documents, documentsOnView);
  }
  return documentsOnView;
}
